import { Component, Vue } from 'vue-property-decorator';
import moment from 'moment';
import { startCase, toLower } from 'lodash';
import { DATE_FORMAT, PAGE_NAMES, PROJECT_ASSESSMENT_STATUSES, TASK_TYPES } from '@/constants';
import { Pagination, Task } from '@/models';
import { fetchTasks } from '@/services';
import ProjectCardsPagination
  from '@/components/pages/project-list/project-cards-pagination/ProjectCardsPagination.vue';
import InlineLoader from '@/components/common/loader/inline-loader/InlineLoader.vue';

@Component({
  components: {
    ProjectCardsPagination,
    InlineLoader
  }
})
export default class MyTasks extends Vue {
  private tasks = [] as Task[];
  private tasksLoading = true;
  private pagination: Pagination = { pageIndex: 1, itemsPerPage: 3, totalLength: 1 };

  get totalPageLength(): number {
    return Math.ceil(this.tasks.length / this.pagination.itemsPerPage) || 1;
  }

  get noTask(): string {
    return this.tasks.length === 0 && !this.tasksLoading ? `Bravo, there is no pending task.` : '';
  }

  getDisplayTaskDescription(task: Task): string {
    let result = '';
    switch (task.type) {
      case TASK_TYPES.HANDLE_PROJECT:
        result = this.getTaskTypeHandleProject(task);
        break;
      case TASK_TYPES.HANDLE_FEEDBACK:
        result = `View ${task.feedbackCount} ${task.feedbackCount === 1 ? 'feedback' : 'feedbacks'}`;
        break;
      case TASK_TYPES.HANDLE_REWARD_REQUEST:
        result = `Handle ${task.requestCount} pending gift ${task.requestCount === 1 ? 'request' : 'requests'}`;
        break;
      case TASK_TYPES.CONFIRM_INDIVIDUAL_RESULTS:
        result = `Confirm Individual Results for Project ${task.projectName}`;
        break;
    }
    return result;
  }

  getTaskTypeHandleProject(task: Task): string {
    const stepDescription = `Complete ${startCase(toLower(task.projectStatus))} for <b>${task.projectName}</b>`;
    switch (task.projectStatus) {
      case PROJECT_ASSESSMENT_STATUSES.MEMBER_CONTRIBUTION_EVALUATION:
        return stepDescription;
      case PROJECT_ASSESSMENT_STATUSES.PROJECT_EVALUATION:
        return stepDescription;
      case PROJECT_ASSESSMENT_STATUSES.PEER_EVALUATION:
        return stepDescription + ' by ' + moment(task.dueDate).format(DATE_FORMAT.DISPLAY);
      case PROJECT_ASSESSMENT_STATUSES.RESULT_RELEASE:
        return `View your result on <b>${task.projectName}</b>`;
      default:
        return stepDescription;
    }
  }

  navigateToOtherPage(task: Task): void {
    switch (task.type) {
      case TASK_TYPES.CONFIRM_INDIVIDUAL_RESULTS:
      case TASK_TYPES.HANDLE_PROJECT:
        this.$router.push(`/project/${task.projectId}`);
        break;
      case TASK_TYPES.HANDLE_FEEDBACK:
        this.$router.push({
          name: PAGE_NAMES.MANAGEMENTS,
          params: { tab: '2' }
        });
        break;
      case TASK_TYPES.HANDLE_REWARD_REQUEST:
        this.$router.push(PAGE_NAMES.REWARD_MANAGEMENT);
        this.$router.push({
          name: PAGE_NAMES.REWARD_PERIOD_DETAIL,
          params: {
            id: '' + task.rewardPeriodId
          }
        });
        break;
    }
  }

  orderTasks(tasks: Task[]): Task[] {
    let prioritizeTasks = [] as Task[];
    const giftRequestTasks = tasks.filter(task => task.type === TASK_TYPES.HANDLE_REWARD_REQUEST);
    const feedbackTasks = tasks.filter(task => task.type === TASK_TYPES.HANDLE_FEEDBACK);
    const memberEvaluationTasks = tasks.filter(
      task => task.projectStatus === PROJECT_ASSESSMENT_STATUSES.MEMBER_CONTRIBUTION_EVALUATION
    );
    const projectEvaluationTasks = tasks.filter(
      task => task.projectStatus === PROJECT_ASSESSMENT_STATUSES.PROJECT_EVALUATION
    );
    // note: This combined Confirm Individual Tasks
    const peerEvaluationTasks = tasks.filter(
      task => task.projectStatus === PROJECT_ASSESSMENT_STATUSES.PEER_EVALUATION
    );
    prioritizeTasks = prioritizeTasks.concat(
      giftRequestTasks,
      feedbackTasks,
      memberEvaluationTasks,
      projectEvaluationTasks,
      peerEvaluationTasks
    );
    const otherTasks = tasks.filter(x => !prioritizeTasks.includes(x));
    const newtasks = [] as Task[];
    return newtasks.concat(prioritizeTasks, otherTasks);
  }

  async created(): Promise<void> {
    const { data } = await fetchTasks();
    this.tasks = this.orderTasks(data) ?? [];
    this.tasksLoading = false;
  }
}
